/**
 * As consultas na tela (#1011): o que a lista, a "Nova consulta" e o "Editar"
 * recebem, e as contas que fazem com isso. Sem React, para o teste não precisar
 * de tela.
 *
 * ── Os componentes ainda não falam com a API ─────────────────────────────
 *
 * O desenho veio do protótipo do Claude Design, e as rotas que vão alimentá-lo
 * ainda vão ser definidas. Por isso os tipos daqui são os da TELA, não os do
 * contrato: datas chegam já escritas ("quinta, 6 de novembro, às 10:00"), e
 * quem ligar as rotas é quem converte o que a API devolve neles.
 */

import {
  ErroDaApi,
  type ConsultaDaOportunidade,
  type DadosDoPacienteParaAgendar,
} from '@cliqcrm/contracts';
import type { OpcaoDaLista } from './formulario-do-agendamento';

/** Um médico ou uma especialidade, como a tela oferece para escolher. */
export type OpcaoDaAgenda = { readonly id: string; readonly nome: string };

/** Uma consulta da lista. */
export type ConsultaNaLista = {
  readonly id: string;
  /** A data e hora por extenso; nula enquanto não foi escolhida. */
  readonly quando: string | null;
  readonly medico: OpcaoDaAgenda | null;
  readonly especialidade: OpcaoDaAgenda | null;
  readonly codigoDoPaciente: string | null;
  readonly codigoDaAgenda: string | null;
  readonly marcada: boolean;
};

/** O paciente que a busca achou no SISAC. Do CPF, só os dois últimos dígitos. */
export type PacienteEncontrado = {
  readonly codigo: string;
  readonly nome: string;
  readonly finalDoCpf: string | null;
};

/** Uma vaga livre: a casa do calendário, o horário e o texto do resumo. */
export type VagaLivre = {
  readonly id: string;
  /** "2026-10-28": o dia no relógio da clínica, que o calendário agrupa. */
  readonly dia: string;
  /** "08:30". */
  readonly hora: string;
  /** "qua, 28/10, 08:30". */
  readonly quando: string;
  /** "quarta, 28 de outubro, às 08:30". */
  readonly quandoPorExtenso: string;
};

/** Em que pé estão as vagas do médico confirmado. */
export type EstadoDasVagas =
  | { readonly situacao: 'carregando' }
  | { readonly situacao: 'erro'; readonly recado: string }
  | {
      readonly situacao: 'pronta';
      readonly lista: readonly VagaLivre[];
      /** Há mais vagas do que as mostradas. */
      readonly cortada: boolean;
      /** A vaga escolhida foi tomada no envio. */
      readonly ocupada: boolean;
    };

/** Em que pé está o envio de "Marcar consulta" ou de "Salvar". */
export type EstadoDoEnvio =
  | { readonly situacao: 'parado' }
  | { readonly situacao: 'enviando' }
  /** O SISAC não respondeu: nada foi gravado, e tentar de novo faz sentido. */
  | { readonly situacao: 'fora-do-ar' }
  /** Outra pessoa mudou a consulta antes: recarregar, e não insistir. */
  | { readonly situacao: 'conflito' }
  | { readonly situacao: 'erro'; readonly recado: string };

/**
 * As opções, mais a que a consulta já tem se ela saiu da lista do SISAC. Sem
 * isso, editar a consulta de um médico que deixou a clínica mostraria o campo
 * vazio, e salvar apagaria quem atendeu sem ninguém ter pedido.
 */
export function opcoesComAAtual(
  opcoes: readonly OpcaoDaAgenda[],
  atual: OpcaoDaAgenda | null,
): OpcaoDaAgenda[] {
  if (!atual || opcoes.some((opcao) => opcao.id === atual.id)) return [...opcoes];
  return [...opcoes, atual];
}

/** Enquanto o SISAC não responde, tudo trava com este motivo. */
export const ESPERA_DO_SISAC = 'Aguarde a resposta do SISAC.';

/** Uma lista do formulário: as opções, ou `null` quando a operação não está ligada. */
export type ListaDoFormulario =
  | { readonly situacao: 'desligada' }
  | { readonly situacao: 'carregando' }
  | { readonly situacao: 'erro'; readonly recado: string }
  | { readonly situacao: 'pronta'; readonly opcoes: readonly OpcaoDaLista[] };

/** O paciente vinculado ao contato, com o cadastro para pré-preencher. */
export type PacienteDaConsulta =
  | { readonly situacao: 'sem-vinculo' }
  | { readonly situacao: 'lendo' }
  | {
      readonly situacao: 'vinculado';
      readonly paciente: PacienteEncontrado;
      readonly dados: DadosDoPacienteParaAgendar | null;
    };

// ── A escrita conferida (#1187) ─────────────────────────────────────────

/**
 * O aviso depois de gravar no sistema da clínica, com o que a conferência
 * disse: conferida, não conferida (a leitura não respondeu) ou sem conferência.
 */
export function avisoDaEscrita(
  feito: 'agendada' | 'atualizada' | 'cancelada',
  conferencia: ConsultaDaOportunidade['conferencia'],
): string {
  const base = `Consulta ${feito} no SISAC`;
  const vaga = feito === 'cancelada' ? ' A vaga ficou livre.' : '';
  if (conferencia === 'conferida') return `${base} e conferida.${vaga}`;
  if (conferencia === 'nao_conferida') {
    return `${base}.${vaga} A conferência não respondeu: confira no sistema da clínica.`;
  }
  return `${base}.${vaga}`;
}

/**
 * Os campos que o sistema da clínica gravou diferente (o 409 da conferência):
 * só os nomes, que o servidor manda; nulo quando o erro é outro.
 */
export function camposQueNaoConferem(erro: unknown): string[] | null {
  if (!(erro instanceof ErroDaApi) || erro.status !== 409) return null;
  const campos = (erro.problema.errors ?? [])
    .filter((e) => e.problema === 'não confere no sistema da clínica')
    .map((e) => e.campo);
  return campos.length > 0 ? campos : null;
}

/** O recado do erro, com os campos que não conferem quando é a conferência. */
export function recadoDaEscrita(recado: string, erro: unknown): string {
  const campos = camposQueNaoConferem(erro);
  return campos ? `${recado} Não conferem: ${campos.join(', ')}.` : recado;
}
